import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Dialog } from "@headlessui/react";
import {
    ExclamationTriangleIcon,
    PencilSquareIcon,
    TrashIcon,
    DocumentTextIcon,
} from "@heroicons/react/24/outline";
import api from "../../api";
import FileInput from "../../components/FileInput";
import Modal from "../../components/Modal";
import Table from "../../components/Table";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import PatientModuleHeader from "../../components/patients/layout/PatientModuleHeader";
import PatientModuleLayout from "../../components/patients/layout/PatientModuleLayout";

const columns = [
    { name: "uploadedBy", displayText: "Subido por", truncate: true },
    { name: "uploadDate", displayText: "Fecha" },
    { name: "name", displayText: "Nombre del archivo", truncate: true },
];

function SummaryChip({ label, value }) {
    return (
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm dark:border-slate-800 dark:bg-slate-950">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                {label}
            </p>
            <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">
                {value || "--"}
            </p>
        </div>
    );
}

function DocumentsMobileList({ documents, onRename, onDelete }) {
    if (!documents.length) {
        return (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-8 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-400">
                No hay archivos adjuntos todavía.
            </div>
        );
    }

    return (
        <div className="space-y-3 lg:hidden">
            {documents.map((document) => (
                <a
                    key={document.id}
                    href={document.document}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950"
                >
                    <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                            <DocumentTextIcon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">
                                {document.name}
                            </p>
                            <div className="mt-3 grid grid-cols-2 gap-3">
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                        Fecha
                                    </p>
                                    <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">
                                        {document.uploadDate}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                        Subido por
                                    </p>
                                    <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">
                                        {document.uploadedBy}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-2">
                        <button
                            type="button"
                            onClick={(event) => {
                                event.preventDefault();
                                onRename(document);
                            }}
                            className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                        >
                            <PencilSquareIcon className="h-4 w-4" />
                            Renombrar
                        </button>
                        <button
                            type="button"
                            onClick={(event) => {
                                event.preventDefault();
                                onDelete(document);
                            }}
                            className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-200"
                        >
                            <TrashIcon className="h-4 w-4" />
                            Eliminar
                        </button>
                    </div>
                </a>
            ))}
        </div>
    );
}

export default function PatientDocumentsPage() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarOpenDesktop, setSidebarOpenDesktop] = useState(true);
    const [patient, setPatient] = useState();
    const [patientDocument, setPatientDocument] = useState({ documents: [] });
    const [loading, setLoading] = useState(true);
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [error, setError] = useState(undefined);
    const [userId] = useState(
        JSON.parse(localStorage.getItem("BDOuserData"))?.id
    );
    const [openEdit, setOpenEdit] = useState(false);
    const [editName, setEditName] = useState("");
    const [selectedDocument, setSelectedDocument] = useState(undefined);
    const [openDelete, setOpenDelete] = useState(false);
    const { idPatient } = useParams();
    const isDesktop = useMediaQuery("(min-width: 1024px)");

    useEffect(() => {
        if (isDesktop) {
            setSidebarOpen(false);
        }
    }, [isDesktop]);

    const pages = [
        { name: "Pacientes", href: "/patients", current: false },
        {
            name: `${patient?.name || "Detalle del paciente"}`,
            href: `/patients/${idPatient}`,
            current: false,
        },
        { name: "Documentos adjuntos", href: "#", current: true },
    ];

    const loadDocuments = useCallback(() => {
        return api.documents
            .getMulti(null, null, null, null, { patient: idPatient })
            .then((response) => {
                setDocuments(
                    response.map((element) => ({
                        id: element.id,
                        name: element.name,
                        uploadedBy: element.uploadedBy
                            ? element.uploadedBy.name
                            : "--",
                        uploadDate: new Date(
                            element.uploadDate
                        ).toLocaleDateString("es-AR"),
                        document: element.document,
                    }))
                );
            });
    }, [idPatient]);

    useEffect(() => {
        Promise.all([api.patients.getOne(idPatient), loadDocuments()])
            .then(([response]) => {
                setPatient({
                    ...response,
                    name: `${response.firstName} ${response.lastName}`,
                });
            })
            .catch((err) => console.log(err))
            .finally(() => setLoading(false));
    }, [idPatient, loadDocuments]);

    const handleSubmit = async () => {
        setLoadingSubmit(true);
        setError(undefined);

        try {
            await Promise.all(
                patientDocument.documents.map((doc) => {
                    const finalName = `${doc.name}.${doc.extension}`;
                    const formData = new FormData();
                    formData.append("patient", idPatient.toString());
                    formData.append("document", doc.file);
                    formData.append("name", finalName);
                    formData.append("uploadedBy", userId);
                    return api.documents.create(formData);
                })
            );

            setPatientDocument({ documents: [] });
            await loadDocuments();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoadingSubmit(false);
        }
    };

    const startEditDocument = (doc) => {
        setSelectedDocument(doc);
        const dotIndex = doc.name.lastIndexOf(".");
        const baseName = dotIndex > 0 ? doc.name.slice(0, dotIndex) : doc.name;
        setEditName(baseName);
        setOpenEdit(true);
    };

    const handleRename = async () => {
        if (!selectedDocument) return;

        try {
            const originalExt = selectedDocument.name.includes(".")
                ? selectedDocument.name.split(".").pop()
                : "";
            const finalName = originalExt
                ? `${editName}.${originalExt}`
                : editName;

            await api.documents.update(selectedDocument.id, { name: finalName }, true);
            await loadDocuments();
            setOpenEdit(false);
            setSelectedDocument(undefined);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleFileSelect = (files) => {
        const MAX_SIZE_BYTES = 20 * 1024 * 1024;
        const tooLarge = files.filter((file) => file.size > MAX_SIZE_BYTES);
        const allowed = files.filter((file) => file.size <= MAX_SIZE_BYTES);

        if (tooLarge.length > 0) {
            const names = tooLarge.map((file) => file.name).join(", ");
            setError({
                document: `El archivo supera el tamaño máximo permitido (20 MB): ${names}`,
            });
        } else {
            setError(undefined);
        }

        if (!allowed.length) return;

        const mapped = allowed.map((file) => ({
            file,
            name: file.name.split(".")[0],
            extension: file.name.split(".").pop(),
        }));

        setPatientDocument((prev) => ({
            ...prev,
            documents: [...prev.documents, ...mapped],
        }));
    };

    const handleDelete = async () => {
        if (!selectedDocument) return;
        try {
            await api.documents.delete(selectedDocument.id);
            await loadDocuments();
            setOpenDelete(false);
            setSelectedDocument(undefined);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleSidebarToggle = () => {
        if (isDesktop) {
            setSidebarOpenDesktop(!sidebarOpenDesktop);
            return;
        }
        setSidebarOpen(!sidebarOpen);
    };

    const editModal = (
        <>
            <div className="mt-3 sm:mt-5">
                <Dialog.Title
                    as="h3"
                    className="text-lg font-semibold text-gray-900 dark:text-white"
                >
                    Renombrar archivo
                </Dialog.Title>
                <div className="mt-4 flex items-center gap-2">
                    <input
                        type="text"
                        value={editName}
                        onChange={(event) => setEditName(event.target.value)}
                        className="min-h-[44px] w-full rounded-xl border border-slate-300 px-3 py-2 text-black"
                    />
                    {selectedDocument && (
                        <span className="text-sm text-gray-500">
                            .{selectedDocument.name.split(".").pop()}
                        </span>
                    )}
                </div>
            </div>
            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <button
                    type="button"
                    className="inline-flex min-h-[44px] items-center justify-center rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700"
                    onClick={() => {
                        setOpenEdit(false);
                        setSelectedDocument(undefined);
                    }}
                >
                    Cancelar
                </button>
                <button
                    type="button"
                    className="inline-flex min-h-[44px] items-center justify-center rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
                    onClick={handleRename}
                >
                    Guardar
                </button>
            </div>
        </>
    );

    const deletionModal = (
        <>
            <div>
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                    <ExclamationTriangleIcon
                        className="h-6 w-6 text-red"
                        aria-hidden="true"
                    />
                </div>
                <div className="mt-3 text-center sm:mt-5">
                    <Dialog.Title
                        as="h3"
                        className="text-lg font-semibold text-gray-900 dark:text-white"
                    >
                        Eliminar archivo
                    </Dialog.Title>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-200">
                        ¿Estás seguro de que quieres eliminar {selectedDocument?.name}?
                    </p>
                </div>
            </div>
            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <button
                    type="button"
                    className="inline-flex min-h-[44px] items-center justify-center rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700"
                    onClick={() => {
                        setOpenDelete(false);
                        setSelectedDocument(undefined);
                    }}
                >
                    Cancelar
                </button>
                <button
                    type="button"
                    className="inline-flex min-h-[44px] items-center justify-center rounded-xl bg-red px-4 py-2 text-sm font-semibold text-white"
                    onClick={handleDelete}
                >
                    Eliminar
                </button>
            </div>
        </>
    );

    return (
        <PatientModuleLayout
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            sidebarOpenDesktop={sidebarOpenDesktop}
            loading={loading}
            header={
                <PatientModuleHeader
                    pages={pages}
                    onToggleSidebar={handleSidebarToggle}
                    mobileBackTo={`/patients/${idPatient}`}
                    mobileBackLabel="Paciente"
                    mobileTitle={patient?.name || "Paciente"}
                />
            }
        >
            <Modal
                size="sm:max-w-lg w-full"
                content={deletionModal}
                open={openDelete}
                setOpen={setOpenDelete}
            />
            <Modal
                size="sm:max-w-lg w-full"
                content={editModal}
                open={openEdit}
                setOpen={setOpenEdit}
            />
            <div className="mx-auto flex w-full max-w-7xl flex-col gap-5 px-4 py-4 lg:px-6 lg:py-6">
                        <section className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950 sm:p-5">
                            <div className="flex flex-col gap-4">
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                                        Documentos adjuntos
                                    </p>
                                    <h1 className="mt-1 text-xl font-semibold text-slate-900 dark:text-white sm:text-2xl">
                                        {patient?.name}
                                    </h1>
                                </div>
                                <div className="grid gap-3 sm:grid-cols-3">
                                    <SummaryChip label="C.I." value={patient?.govId} />
                                    <SummaryChip label="Edad" value={patient?.age ? `${patient.age} años` : "--"} />
                                    <SummaryChip label="Total de archivos" value={documents.length} />
                                </div>
                            </div>
                        </section>

                        <section className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950 sm:p-5">
                            <div className="grid gap-5 xl:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)]">
                                <div>
                                    <h2 className="text-sm font-semibold uppercase tracking-[0.12em] text-slate-600 dark:text-slate-300">
                                        Cargar archivos
                                    </h2>
                                    <div className="mt-4">
                                        <FileInput
                                            errorMessage={error?.document}
                                            multiple={true}
                                            labelClassName="text-sm font-medium text-slate-700 dark:text-slate-200"
                                            label="Archivo"
                                            buttonText="Adjuntar archivo"
                                            handleChange={(name, files) => handleFileSelect(files)}
                                            name="document"
                                        />
                                    </div>
                                </div>

                                <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-800 dark:bg-slate-900/50">
                                    <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                                        Archivos pendientes
                                    </h3>
                                    <div className="mt-4 space-y-3">
                                        {patientDocument.documents.length > 0 ? (
                                            patientDocument.documents.map((doc, index) => (
                                                <div
                                                    key={`${doc.name}-${doc.extension}-${index}`}
                                                    className="rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-950"
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="text"
                                                            value={doc.name}
                                                            onChange={(event) => {
                                                                const updated = [...patientDocument.documents];
                                                                updated[index].name = event.target.value;
                                                                setPatientDocument({
                                                                    ...patientDocument,
                                                                    documents: updated,
                                                                });
                                                            }}
                                                            className="min-h-[40px] w-full rounded-lg border border-slate-300 px-3 py-2 text-black"
                                                        />
                                                        <span className="text-sm text-slate-500">
                                                            .{doc.extension}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                                Aún no hay archivos seleccionados.
                                            </p>
                                        )}
                                    </div>

                                    <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                                        <button
                                            type="button"
                                            onClick={handleSubmit}
                                            disabled={
                                                loadingSubmit ||
                                                patientDocument.documents.length === 0
                                            }
                                            className="inline-flex min-h-[44px] items-center justify-center rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            {loadingSubmit ? "Subiendo..." : "Añadir"}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setPatientDocument({ documents: [] });
                                                setError(undefined);
                                            }}
                                            disabled={loadingSubmit}
                                            className="inline-flex min-h-[44px] items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950 sm:p-5">
                            <div className="mb-4">
                                <h2 className="text-sm font-semibold uppercase tracking-[0.12em] text-slate-600 dark:text-slate-300">
                                    Archivos adjuntos
                                </h2>
                            </div>

                            <DocumentsMobileList
                                documents={documents}
                                onRename={startEditDocument}
                                onDelete={(doc) => {
                                    setSelectedDocument(doc);
                                    setOpenDelete(true);
                                }}
                            />

                            {isDesktop && (
                                <div className="hidden lg:block">
                                    <Table
                                        actiontext="Ver"
                                        delete={true}
                                        setSelectedDocument={setSelectedDocument}
                                        setOpenDelete={setOpenDelete}
                                        document={true}
                                        columns={columns}
                                        rows={documents}
                                        openModal={startEditDocument}
                                        openModalText="Renombrar"
                                        actionCellClassName="px-2 py-1 whitespace-nowrap text-center text-sm"
                                    />
                                </div>
                            )}
                        </section>
            </div>
        </PatientModuleLayout>
    );
}
